// main tools
import { useTranslation } from 'react-i18next'
import { useState } from 'react'

// components
import { ChangeLanguage } from '@dr-keyword-molecules/change-language'
import { IconComponent } from '@centria-ui-atoms/icon-component'
import { Section } from '@centria-ui/components/atoms/section'
import { Typography } from '@centria-ui-atoms/typography'
import { Button } from '@centria-ui-atoms/button'
import { Input } from '@centria-ui-atoms/input'

// hooks
import { useWindowSize } from '@centria-ui/hooks/use-window-size'

// services
import { LoginPageService } from '@dr-keyword-services/login'

// styles
import classes from './styles.module.scss'

// types
import type { ChangeType, SubmitType } from '@centria-ui/types'
import type { FC } from 'react'

type RequestPasswordResetFormProps = {
  handleShowForgottenPassword: () => void
}

export const RequestPasswordResetForm: FC<RequestPasswordResetFormProps> = ({
  handleShowForgottenPassword
}) => {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const { screenWidth } = useWindowSize()
  const [loading, setLoading] = useState(false)
  const { showSuccess, showInfo } = LoginPageService.getBaseService()
  const { requestPasswordReset } = LoginPageService.requestPasswordReset()

  /**
   * @description Handles the change email
   */
  const handleChange = (ev: ChangeType) => setEmail(ev.target.value)

  /**
   * @description Handles the submit of the form
   */
  const handleSubmit = async (ev: SubmitType) => {
    ev.preventDefault()

    setLoading(true)
    showInfo(t('alerts.forgot-password.loading'))

    await requestPasswordReset({
      variables: { email },
      onCompleted: () => showSuccess(t('alerts.forgot-password.success'))
    })

    setLoading(false)
  }

  return (
    <Section size='lg' onSubmit={handleSubmit} as='form'>
      <Typography
        Tag='h2'
        fontSize='subtitle'
        fontcolor='primary'
        fontWeight='semiBold'
        className={classes.subtitle}>
        {t('forgot-password-page.title')}
      </Typography>
      <Input
        name='email'
        value={email}
        className='mb-4'
        id='change-email'
        inputColor='light'
        inputVariant='block'
        onChange={handleChange}
        label={t('forgot-password-page.email')}
      />
      <Button
        type='submit'
        loading={loading}
        variant='secondary'
        className={classes.button}>
        {t('forgot-password-page.action-button')}
      </Button>
      <Typography
        fontSize='small'
        onClick={handleShowForgottenPassword}
        className={`${classes.actionLabel} ${screenWidth < 768 ? 'my-3' : ''}`}>
        <IconComponent className='me-2' icon='LockIcon' color='inherit' />
        {t('forgot-password-page.login')}
      </Typography>
      <div className='d-flex justify-content-center mt-2'>
        <ChangeLanguage />
      </div>
    </Section>
  )
}
